/**
 * User Matrix role permission catalog (settings UI + profile "Your capabilities").
 * Keep keys in sync with backend normalizeRolePermissions in index.js.
 */

export const ROLE_PERMISSION_OPTIONS = [
  // Board / app access
  { key: "canAccessCommercial", label: "Can access Commercial project board", group: "Board access" },
  { key: "canAccessExpansion", label: "Can access Expansion project board", group: "Board access" },
  { key: "canAccessProposalBuilder", label: "Can access Proposal Builder", group: "Board access" },
  { key: "canAccessReporting", label: "Can access Reporting", group: "Board access" },
  { key: "canAccessContracts", label: "Can access Contracts", group: "Board access" },
  { key: "canAccessCrm", label: "Can access CRM", group: "Board access" },
  { key: "canAccessMaterialsInventory", label: "Can access Materials (inventory & board columns)", group: "Board access" },
  {
    key: "canAccessDatacenterLayout",
    label: "Can access Datacenter layout planner",
    group: "Board access",
  },
  { key: "canAccessShop", label: "Can access Shop", group: "Board access" },
  { key: "canAccessMessages", label: "Can access Messages inbox", group: "Board access" },
  {
    key: "canAccessScheduleCalendar",
    label: "Can access schedule calendar",
    group: "Board access",
  },
  {
    key: "canAccessCommercialMap",
    label: "Can access commercial map",
    group: "Board access",
  },
  { key: "canAccessSettings", label: "Can access Settings", group: "Board access" },

  // Administration
  { key: "canViewMailLog", label: "Can view outgoing mail log", group: "Administration" },
  { key: "canManagePlugins", label: "Can manage plugin toggles", group: "Administration" },
  { key: "canManageUserMatrix", label: "Can manage User Matrix settings", group: "Administration" },

  // Commercial / expansion landing edits
  {
    key: "canEditCommercialOverview",
    label: "Can edit commercial project Overview",
    group: "Project landing",
  },
  {
    key: "canEditPermittingTab",
    label: "Can edit Permitting tab (and Overview)",
    group: "Project landing",
  },
  { key: "canEditEngineeringTab", label: "Can edit Engineering tab", group: "Project landing" },
  {
    key: "canEditOspUploads",
    label: "Can upload site walk and as-built files (OSP)",
    group: "Project landing",
  },
  {
    key: "canEditConstructionUploads",
    label: "Can upload Construction as-built files",
    group: "Project landing",
  },
  {
    key: "canEditUsaTickets",
    label: "Can upload 811 / USA ticket files",
    group: "Project landing",
  },
  {
    key: "canEditInspectorDailyProgress",
    label: "Can edit Inspector daily progress reports",
    group: "Project landing",
  },
  { key: "canEditNetworkingTab", label: "Can edit Networking tab", group: "Project landing" },
  { key: "canEditFiberTab", label: "Can edit Fiber tab", group: "Project landing" },
  { key: "canEditAccountingTab", label: "Can edit Accounting tab", group: "Project landing" },
  {
    key: "canCloseCommercialProject",
    label: "Can close commercial projects",
    group: "Project landing",
  },
  {
    key: "canCreateCommercialProject",
    label: "Can create commercial projects",
    group: "Project landing",
  },

  // Landing tab visibility
  { key: "canViewLandingTabOverview", label: "Can view Overview tab", group: "Project landing tabs" },
  {
    key: "canViewLandingTabEngineering",
    label: "Can view Engineering tab",
    group: "Project landing tabs",
  },
  {
    key: "canViewLandingTabPermitting",
    label: "Can view Permitting tab",
    group: "Project landing tabs",
  },
  {
    key: "canViewLandingTabConstruction",
    label: "Can view Construction tab",
    group: "Project landing tabs",
  },
  { key: "canViewLandingTabFiber", label: "Can view Fiber tab", group: "Project landing tabs" },
  {
    key: "canViewLandingTabNetworking",
    label: "Can view Networking tab",
    group: "Project landing tabs",
  },
  {
    key: "canViewLandingTabAccounting",
    label: "Can view Accounting tab",
    group: "Project landing tabs",
  },

  // Schedules
  { key: "canEditConstructionSchedule", label: "Can edit Construction schedule", group: "Schedules" },
  { key: "canEditFiberSchedule", label: "Can edit Fiber schedule", group: "Schedules" },
  { key: "canEditNetworkingSchedule", label: "Can edit Networking schedule", group: "Schedules" },
];

/** Maps landing tab ids → User Matrix view-permission keys. */
export const LANDING_TAB_VIEW_PERMISSION_KEYS = {
  summary: "canViewLandingTabOverview",
  engineering: "canViewLandingTabEngineering",
  permitting: "canViewLandingTabPermitting",
  construction: "canViewLandingTabConstruction",
  fiber: "canViewLandingTabFiber",
  networking: "canViewLandingTabNetworking",
  accounting: "canViewLandingTabAccounting",
};

export const ROLE_PERMISSION_KEYS = ROLE_PERMISSION_OPTIONS.map((o) => o.key);

export const DEFAULT_ROLE_PERMISSIONS = Object.fromEntries(
  ROLE_PERMISSION_KEYS.map((key) => {
    if (
      key === "canAccessCommercial" ||
      key === "canAccessExpansion" ||
      key === "canAccessShop" ||
      key === "canAccessMessages" ||
      key === "canAccessScheduleCalendar" ||
      key === "canAccessCommercialMap" ||
      key === "canAccessSettings"
    ) {
      return [key, true];
    }
    if (key.startsWith("canViewLandingTab")) return [key, true];
    return [key, false];
  })
);

/** Group options for User Matrix Permissions UI. */
export function rolePermissionOptionsByGroup() {
  const groups = [];
  const byName = new Map();
  for (const opt of ROLE_PERMISSION_OPTIONS) {
    const g = opt.group || "Other";
    if (!byName.has(g)) {
      const entry = { group: g, options: [] };
      byName.set(g, entry);
      groups.push(entry);
    }
    byName.get(g).options.push(opt);
  }
  return groups;
}
